import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications?limit=20');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* ignore — user might not be authed yet */ }
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');

    try {
      const ws = new WebSocket(`${wsUrl}/ws/notifications?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Keep alive with pings every 30s
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.event === 'connected') {
            setUnreadCount(data.unread_count || 0);
          } else if (data.event === 'notification') {
            const notif = data.data;
            setNotifications((prev) => [notif, ...prev].slice(0, 30));
            setUnreadCount((prev) => prev + 1);

            // Show toast for high-priority types
            const toastTypes = ['new_message', 'new_match', 'event_payment', 'content_flagged'];
            if (toastTypes.includes(notif.type)) {
              toast(notif.title, { description: notif.body, duration: 5000 });
            }
          }
        } catch { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        wsRef.current = null;
        clearInterval(pingTimer.current);
        // Reconnect after 5 seconds
        reconnectTimer.current = setTimeout(() => {
          if (token) connectWebSocket();
        }, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch { /* ignore ws errors */ }
  }, [token]);

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchNotifications();
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      clearInterval(pingTimer.current);
      clearTimeout(reconnectTimer.current);
    };
  }, [isAuthenticated, token, fetchNotifications, connectWebSocket]);

  // Disconnect when logged out
  useEffect(() => {
    if (!isAuthenticated) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  const markRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications');
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      markRead,
      markAllRead,
      clearAll,
      fetchNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
